// ─── English (en) resource catalog ──────────────────────────────────────────
//
// Single source of truth for translatable copy. Keys are namespaced by surface
// so a translator (or a future locale file) can see what belongs together.
//
// Currently populated for the two audited admin surfaces only:
//   • admin.cardsStacks.*  — the "Cards and Stacks" section (type grid +
//                            the card/stack type editor dialog and its
//                            States / Dynamics / Fields editors)
//   • admin.sceneRecording.* — the "Scene Recording" section (default board /
//                            deck pickers + the AI scene-processing controls)
//
// Interpolation uses i18next's `{{var}}` syntax. Plurals use the `_one` /
// `_other` suffix convention (i18next v23 JSON v4).

const en = {
  admin: {
    // ── Cards and Stacks ──────────────────────────────────────────────────
    cardsStacks: {
      // Section header (categories tab)
      title: "Cards and Stacks",
      intro:
        "Define card and stack types for content creation. Manage user categories on the Members page and subclan categories on the Subclans page.",

      // Two-column grid (per-clan column titles)
      cardsColumnTitle: "{{clan}} Cards",
      stacksColumnTitle: "{{clan}} Stacks",

      // Add-new row
      add: "Add",
      newCardPlaceholder: "New card type (e.g. Job Opening)...",
      newStackPlaceholder: "New stack type (e.g. Hiring Pipeline)...",
      emptyCards: "No card types defined yet",
      emptyStacks: "No stack types defined yet",

      // Add-validation + cap
      duplicateCardName: "A card type with this name already exists.",
      duplicateStackName: "A stack type with this name already exists.",
      cardCapError: "You can define at most {{max}} card types.",
      stackCapError: "You can define at most {{max}} stack types.",
      capNote_card:
        "You’ve reached the maximum of {{max}} card types. Remove one to add another.",
      capNote_stack:
        "You’ve reached the maximum of {{max}} stack types. Remove one to add another.",

      // Summary card meta (field / allowed-type counts)
      unnamed: "(Unnamed)",
      fieldCount_one: "{{count}} field",
      fieldCount_other: "{{count}} fields",
      allowedCount_one: "{{count}} allowed card type",
      allowedCount_other: "{{count}} allowed card types",
      removeTypeTitle: "Remove type",

      // Delete-type confirmation
      deleteCardConfirmTitle: "Delete card type?",
      deleteStackConfirmTitle: "Delete stack type?",
      deleteTypeConfirmMessage:
        "\"{{name}}\" will be deleted immediately and can't be undone.",
      deleteConfirmLabel: "Delete",
      deleteCancelLabel: "Cancel",

      // Persist toasts (call-site messages)
      cardTypeDeleted: "Card type deleted.",
      stackTypeDeleted: "Stack type deleted.",
      cardTypeSaved: "Card type saved.",
      stackTypeSaved: "Stack type saved.",

      // ── Type editor dialog ──────────────────────────────────────────────
      dialog: {
        eyebrowCard: "Card type",
        eyebrowStack: "Stack type",
        descriptionCard: "Configure this card type and its custom fields.",
        descriptionStack:
          "Configure this stack type, which card types it accepts, and its custom fields.",

        // Basic info
        nameLabel: "Name",
        namePlaceholder: "Type name",
        duplicateNameError: "Another {{kind}} already uses this name.",
        kindCard: "card type",
        kindStack: "stack type",
        iconLabel: "Icon",
        iconPlaceholder: "fa-briefcase or emoji",
        descriptionLabel: "Description",
        descriptionPlaceholder: "Shown when users pick this type",

        // Stack-only: allowed card types
        allowedCardTypesTitle: "Allowed Card Types Inside",
        allowedCardTypesEmpty:
          "Create Card Types first to choose which ones can go inside this stack type.",
        maxCardsLabel: "Max cards (0 = unlimited)",
        orderingPolicyLabel: "Ordering policy",

        // Fields editor
        fieldsTitle: "Fields",
        addField: "Add field",
        fieldTypesNote:
          "Field types are defined platform-wide. Manage them in Platform Settings → Field Types.",
        fieldsEmpty: 'No fields yet. Click "Add field" to create one.',

        // Footer actions
        done: "Done",
        save: "Save",
        saving: "Saving…",

        // Delete-field confirmation (TypeDetailDialog)
        deleteFieldConfirmTitle: "Delete field?",
        deleteFieldConfirmMessage:
          "The \"{{label}}\" field and its settings will be removed. This can't be undone.",
        fieldUnnamed: "(unnamed)",
      },

      // ── Stack ordering options ──────────────────────────────────────────
      ordering: {
        manual: "Manual",
        createdAsc: "Created (oldest first)",
        createdDesc: "Created (newest first)",
        alpha: "Alphabetical",
      },

      // ── States editor (lifecycle) ───────────────────────────────────────
      states: {
        title: "States",
        help:
          "States are type-specific tags an item of this type can be in (e.g. Processed, Clean). Type one and press Enter to add it as a tag. Click a state to make it the default that new items start in.",
        empty: "No states yet — add tags like Processed or Clean below.",
        defaultTitle: "Default state — new items start here",
        makeDefaultTitle: "Click to make this the default state",
        removeTitle: "Remove state",
        removeAria: "Remove {{state}}",
        addPlaceholder: "Add a state (e.g. Processed)",
        addAria: "Add a state",
        addButton: "Add state",
      },

      // ── Dynamics editor ─────────────────────────────────────────────────
      dynamics: {
        title: "Dynamics",
        help:
          "Dynamics are existing clan attributes this type tracks. Pick which ones apply.",
        loading: "Loading clan dynamics…",
        loadError: "Failed to load clan dynamics.",
        empty:
          "This clan has no dynamics yet. Create them under Roles → Dynamics, then come back to bind them to this type.",
        boundTitle: "Bound to this type — click to remove",
        bindTitle: "Click to bind this dynamic to this type",
      },

      // ── Per-field editor ────────────────────────────────────────────────
      field: {
        labelPlaceholder: "Field label (e.g. Salary range)",
        typeTitle: "Field type from the platform catalog",
        typeAria: "Field type",
        loadingTypes: "Loading types...",
        required: "Required",
        defaultValuePlaceholder: "Default value",
        defaultValueAria: "Default value",
        helpTextPlaceholder: "Help text",
        helpTextAria: "Help text",
        optionsNotePrefix:
          'Options for "{{type}}" are managed in Platform Settings → Field Types:',
        optionsNoneDefined: "no options defined",
        moveUpTitle: "Move up",
        moveUpAria: "Move field up",
        moveDownTitle: "Move down",
        moveDownAria: "Move field down",
        removeField: "Remove field",
      },
    },

    // ── Scene Recording ───────────────────────────────────────────────────
    sceneRecording: {
      title: "Scene Recording",
      intro:
        "How the mobile app's scans and scene recordings become cards. Pick the default board and deck where they land, then tune how the AI turns a recorded scene into cards.",
      loadError: "Failed to load the recording destination.",
      saveError: "Failed to save the recording destination.",
      saveSuccess: "Scene Recording saved.",
      accessDenied: "Access denied. Admin privileges required.",

      // Board / deck pickers
      boardLabel: "Board",
      boardEmpty: "No boards available",
      boardHint:
        "The board where scanned and Scene-chat cards land by default. Empty falls back to the Home board.",
      deckLabel: "Deck",
      deckEmpty: "No decks available",
      deckHint:
        "The deck within that board where recorded cards are filed. Empty falls back to the board’s first (Open) deck.",

      // AI scene-processing block
      aiTitle: "AI scene processing",
      aiIntro:
        "When someone records a scene in the mobile app, decide how the AI splits the scene into cards and whether those cards take on a clan card type.",

      createModeLabel: "When a scene is recorded",
      createModeImmediate: "Create cards immediately",
      createModeHint: "Cards go live as soon as the AI interprets the scene.",

      groupingLabel: "How to group items",
      groupingCardPerItem: "One card per item",
      groupingSingleCard: "Single card for the scene",
      groupingDeckIfRelated: "Group related items into a deck",
      groupingHint:
        "How interpreted items are laid out: one card per detected item, a single card summarising the whole scene, or one card per item collected under a named deck when the AI judges them related.",

      typingLabel: "Card type",
      typingGeneric: "Generic cards",
      typingAuto: "Auto-match a card type",
      typingHint:
        "Generic makes plain cards with no type binding. Auto tries to match each item to a clan card type by name and stamps its template and default state, falling back to generic when nothing matches.",
      typingNoTypesNote:
        "This clan has no card types yet, so auto-match has nothing to match against. Add card types in the Cards & Stacks editor above first.",

      defaultTypeLabel: "Default card type",
      defaultTypeNone: "No default (let AI decide)",
      defaultTypeHint:
        "When auto-match fails to find a type (or as an explicit default), the card type to bind new scene cards to. Empty means none.",

      // Save controls
      save: "Save",
      saving: "Saving...",
      saved: "Saved",
      saveFailed: "Save failed",
    },
  },
};

export default en;
